import React, { useRef, useState } from 'react';
import ToolLayout from 'tools/ToolLayout';
import FileDropZone from 'tools/FileDropZone';
import {
  btnRow,
  card,
  chipStyle,
  dangerBtn,
  errorBox,
  formatMb,
  hint,
  label,
  primaryBtn,
  privacy,
  secondaryBtn,
  warnBox,
} from 'tools/toolStyles';
import {
  downloadBlob,
  ensureFFmpeg,
  extFromName,
  getWin,
  hasSharedArrayBuffer,
  replaceExt,
  revokeUrl,
  runFFmpegJob,
} from 'tools/ffmpegShared';

type VideoFmt = 'mp4' | 'webm' | 'gif' | 'mp3' | 'wav';

const WARN_MB = 100;
const WARN_SEC = 120;

const FORMATS: {
  id: VideoFmt;
  label: string;
  ext: string;
  mime: string;
  isAudio?: boolean;
  args: (inName: string, outName: string) => string[];
}[] = [
  {
    id: 'mp4',
    label: 'MP4',
    ext: 'mp4',
    mime: 'video/mp4',
    args: (i, o) => [
      '-i', i,
      '-c:v', 'libx264', '-preset', 'ultrafast', '-crf', '28',
      '-c:a', 'aac', '-b:a', '128k',
      '-movflags', '+faststart',
      o,
    ],
  },
  {
    id: 'webm',
    label: 'WebM',
    ext: 'webm',
    mime: 'video/webm',
    args: (i, o) => [
      '-i', i,
      '-c:v', 'libvpx', '-b:v', '1M', '-row-mt', '1',
      '-c:a', 'libvorbis',
      o,
    ],
  },
  {
    id: 'gif',
    label: 'GIF',
    ext: 'gif',
    mime: 'image/gif',
    args: (i, o) => [
      '-i', i,
      '-vf', 'fps=10,scale=320:-1:flags=lanczos',
      '-an',
      o,
    ],
  },
  {
    id: 'mp3',
    label: 'Tách MP3',
    ext: 'mp3',
    mime: 'audio/mpeg',
    isAudio: true,
    args: (i, o) => ['-i', i, '-vn', '-c:a', 'libmp3lame', '-b:a', '192k', o],
  },
  {
    id: 'wav',
    label: 'Tách WAV',
    ext: 'wav',
    mime: 'audio/wav',
    isAudio: true,
    args: (i, o) => ['-i', i, '-vn', '-c:a', 'pcm_s16le', o],
  },
];

const ConvertVideo = () => {
  const videoRef = useRef<any>(null);
  const abortRef = useRef({ aborted: false });
  const [file, setFile] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [fmt, setFmt] = useState<VideoFmt>('mp4');
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [outUrl, setOutUrl] = useState<string | null>(null);
  const [outBlob, setOutBlob] = useState<any>(null);
  const [outName, setOutName] = useState('');
  const [outIsAudio, setOutIsAudio] = useState(false);

  const sab = hasSharedArrayBuffer();
  const win = getWin();

  const applyFile = (f: any) => {
    if (!f) return;
    revokeUrl(previewUrl);
    revokeUrl(outUrl);
    setFile(f);
    setPreviewUrl(win.URL.createObjectURL(f));
    setDuration(0);
    setError('');
    setOutUrl(null);
    setOutBlob(null);
  };

  const onMeta = () => {
    const v = videoRef.current;
    if (v && v.duration) setDuration(v.duration);
  };

  const cancel = () => {
    abortRef.current.aborted = true;
    setStatus('Đang hủy…');
  };

  const run = async () => {
    if (!file) {
      setError('Hãy chọn video.');
      return;
    }
    const spec = FORMATS.find((f) => f.id === fmt)!;
    abortRef.current = { aborted: false };
    setBusy(true);
    setProgress(0);
    setError('');
    revokeUrl(outUrl);
    setOutUrl(null);
    setOutBlob(null);
    try {
      await ensureFFmpeg(setStatus);
      setStatus('Đang chuyển đổi…');
      const inExt = extFromName(file.name, 'mp4');
      const inName = `input.${inExt}`;
      const outFile = `output.${spec.ext}`;
      const blob = await runFFmpegJob(
        file,
        inName,
        outFile,
        spec.args(inName, outFile),
        spec.mime,
        {
          onProgress: setProgress,
          signal: abortRef.current,
          onStatus: setStatus,
        }
      );
      if (abortRef.current.aborted) {
        setStatus('Đã hủy.');
        return;
      }
      const name = replaceExt(file.name, spec.ext);
      const url = win.URL.createObjectURL(blob);
      setOutBlob(blob);
      setOutUrl(url);
      setOutName(name);
      setOutIsAudio(!!spec.isAudio);
      setProgress(1);
      setStatus('Xong — tải file bên dưới.');
    } catch (err: any) {
      if (abortRef.current.aborted) setStatus('Đã hủy.');
      else {
        setError((err && err.message) || 'Chuyển đổi thất bại.');
        setStatus('');
      }
    } finally {
      setBusy(false);
    }
  };

  const sizeWarn =
    (file && file.size > WARN_MB * 1024 * 1024
      ? `File lớn (~${formatMb(file.size)} MB). Gợi ý ≤ ${WARN_MB} MB. `
      : '') +
    (duration > WARN_SEC
      ? `Video dài (~${Math.round(duration)}s). Gợi ý ≤ ${WARN_SEC}s.`
      : '');

  return (
    <ToolLayout title="Đổi đuôi video">
      <p style={privacy}>
        Video được xử lý hoàn toàn trên máy bạn (FFmpeg.wasm), không gửi lên server language123.net.
      </p>

      {!sab ? (
        <p style={warnBox}>
          Trang chưa bật SharedArrayBuffer. Hãy mở trực tiếp{' '}
          <a href="/tools/convert-video">/tools/convert-video</a> (tải lại trang đầy đủ).
        </p>
      ) : null}

      <div style={card}>
        <label style={label}>1. Chọn video</label>
        <p style={hint}>MP4 / WebM / MOV / MKV · gợi ý clip ≤ 1–2 phút, ≤ ~{WARN_MB} MB.</p>
        <FileDropZone
          accept="video/*"
          hintText="Kéo thả hoặc bấm để chọn video"
          onFiles={(files) => applyFile(files[0])}
        />
        {previewUrl ? (
          <video
            ref={videoRef}
            src={previewUrl}
            controls
            muted
            playsInline
            onLoadedMetadata={onMeta}
            style={{ width: '100%', marginTop: 12, borderRadius: 8, background: '#000', maxHeight: 360 }}
          />
        ) : null}
        {file ? (
          <p style={{ ...hint, marginTop: 8, marginBottom: 0 }}>
            {file.name} · {formatMb(file.size)} MB
            {duration > 0 ? ` · ${Math.round(duration)}s` : ''}
          </p>
        ) : null}
        {sizeWarn ? <p style={{ ...warnBox, marginTop: 12 }}>{sizeWarn}</p> : null}
      </div>

      <div style={card}>
        <label style={label}>2. Định dạng xuất</label>
        <div style={btnRow}>
          {FORMATS.map((f) => (
            <button key={f.id} type="button" style={chipStyle(fmt === f.id)} onClick={() => setFmt(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        {fmt === 'gif' ? <p style={hint}>GIF: 10fps, rộng 320px — phù hợp clip rất ngắn.</p> : null}
      </div>

      <div style={card}>
        <div style={btnRow}>
          <button type="button" style={secondaryBtn} disabled={busy || !sab} onClick={() => ensureFFmpeg(setStatus).catch((e: any) => setError(e.message))}>
            Tải FFmpeg trước
          </button>
          <button type="button" style={primaryBtn} disabled={!file || busy || !sab} onClick={run}>
            {busy ? `Đang chuyển… ${Math.round(progress * 100)}%` : 'Chuyển đổi'}
          </button>
          {busy ? (
            <button type="button" style={dangerBtn} onClick={cancel}>
              Hủy
            </button>
          ) : null}
          {outBlob ? (
            <button type="button" style={primaryBtn} onClick={() => downloadBlob(outBlob, outName)}>
              Tải về
            </button>
          ) : null}
        </div>
        {busy || progress > 0 ? (
          <div style={{ height: 8, background: '#eceff1', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ width: `${Math.round(progress * 100)}%`, height: '100%', background: '#1976d2' }} />
          </div>
        ) : null}
        {status ? <p style={hint}>{status}</p> : null}
        {error ? <p style={errorBox}>{error}</p> : null}
        {outUrl && outIsAudio ? <audio src={outUrl} controls style={{ width: '100%', marginTop: 8 }} /> : null}
        {outUrl && !outIsAudio && fmt === 'gif' ? (
          <img src={outUrl} alt="gif result" style={{ maxWidth: '100%', marginTop: 8, borderRadius: 8 }} />
        ) : null}
        {outUrl && !outIsAudio && fmt !== 'gif' ? (
          <video src={outUrl} controls style={{ width: '100%', marginTop: 8, borderRadius: 8, background: '#000' }} />
        ) : null}
      </div>
    </ToolLayout>
  );
};

export default ConvertVideo;
